import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import {
  institutionResetTokenVerificationCall,
  institutionResetPasswordCall
} from "../../helper/apicalls/institution/login";
import EdluminaLogo from "../../assets/png/edlumina-logo-1.png";
import "../../styles/common/login.scss";
import "../../styles/common/resetPassword.scss";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("token");

  const [verifying, setVerifying] = useState(true);
  const [changeToken, setChangeToken] = useState(null);
  const [verificationError, setVerificationError] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const adminToken = Cookies.get("admin_token");
    const instToken = Cookies.get("institution_token");
    const teacherToken = Cookies.get("teacher_token");

    if (instToken) {
      navigate("/institution/dashboard");
      return;
    }
    if (teacherToken) {
      navigate("/teacher/dashboard");
      return;
    }

    const verifyToken = async () => {
      if (!resetToken) {
        setVerificationError("No reset token provided. Please request a new password reset link.");
        setVerifying(false);
        return;
      }
      try {
        const response = await institutionResetTokenVerificationCall(resetToken);
        if (response.verified && response.change_password_token) {
          setChangeToken(response.change_password_token);
        } else {
          setVerificationError(response.message || "Token verification failed.");
        }
      } catch (err) {
        console.error("Token verification error:", err);
        setVerificationError(
          err.message ||
            "The password reset link is invalid or has expired."
        );
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [resetToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) return;

    if (newPassword.length < 6) {
      setSubmitError("Password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setSubmitError("Passwords do not match.");
      return;
    }

    setSubmitError("");
    setSubmitLoading(true);

    try {
      await institutionResetPasswordCall(changeToken, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (err) {
      console.error("Reset password failed:", err);
      setSubmitError(
        err.message ||
          "Failed to reset password. Please try again."
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-logo-wrapper">
        <img src={EdluminaLogo} alt="Edlumina logo" className="login-logo" />
      </div>
      <div className="login-card" role="dialog" aria-labelledby="reset-title">
        {verifying ? (
          <div className="reset-verifying-wrapper">
            <div className="spinner"></div>
            <h2 className="verifying-text">
              Verifying reset link...
            </h2>
          </div>
        ) : verificationError ? (
          <div>
            <h2 id="reset-title" className="title reset-error-title">
              Link Expired
            </h2>
            <p className="reset-description">
              {verificationError}
            </p>
            <button
              onClick={() => navigate("/")}
              className="btn-primary"
            >
              Go to Sign In
            </button>
          </div>
        ) : success ? (
          <div>
            <h2 id="reset-title" className="title reset-success-title">
              Success!
            </h2>
            <p className="reset-description">
              Your password has been reset successfully. Redirecting you to the Sign In page...
            </p>
          </div>
        ) : (
          <div>
            <h2 id="reset-title" className="title">
              Reset Password
            </h2>
            <p className="reset-submit-desc">
              Please enter your new password below.
            </p>

            <form className="login-form" onSubmit={handleSubmit}>
              {submitError && <div className="form-error">{submitError}</div>}

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <div className="password-input">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="newPassword"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    disabled={submitLoading}
                  />
                  <button
                    type="button"
                    className="toggle-visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={submitLoading}
                  >
                    {showPassword ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5C6.5 5 2.1 8.6 1 12c1.1 3.4 5.5 7 11 7s9.9-3.6 11-7c-1.1-3.4-5.5-7-11-7z" stroke="currentColor" strokeWidth="1.5" fill="none" />
                        <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M12 5C6.5 5 2.1 8.6 1 12c.49 1.5 1.67 3.1 3.36 4.41M20.64 16.41C22.33 15.1 23.51 13.5 24 12c-1.1-3.4-5.5-7-12-7" stroke="currentColor" strokeWidth="1.5" fill="none" />
                        <path d="M9.5 9.5a3.5 3.5 0 004.95 4.95" stroke="currentColor" strokeWidth="1.5" fill="none" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="password-input">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={submitLoading}
                  />
                  <button
                    type="button"
                    className="toggle-visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={submitLoading}
                  >
                    {showConfirmPassword ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5C6.5 5 2.1 8.6 1 12c1.1 3.4 5.5 7 11 7s9.9-3.6 11-7c-1.1-3.4-5.5-7-11-7z" stroke="currentColor" strokeWidth="1.5" fill="none" />
                        <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M12 5C6.5 5 2.1 8.6 1 12c.49 1.5 1.67 3.1 3.36 4.41M20.64 16.41C22.33 15.1 23.51 13.5 24 12c-1.1-3.4-5.5-7-12-7" stroke="currentColor" strokeWidth="1.5" fill="none" />
                        <path d="M9.5 9.5a3.5 3.5 0 004.95 4.95" stroke="currentColor" strokeWidth="1.5" fill="none" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button type="submit" className="btn-primary" disabled={submitLoading}>
                {submitLoading ? "Updating..." : "Reset Password"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
